/**
 * SAPIENT detection presentation helpers (REQ-UI-061).
 *
 * The Detections panel rows — and (PR5b) the Map detection-glyph
 * tooltip — both render a detection's confidence and classification, so
 * the null-aware formatting lives here once (CLAUDE.md §4.4 — no
 * duplicated logic) rather than inline in each consumer. Unlike a priority
 * → label + colour mapping, detections carry no priority, so this helper
 * covers the detection row's domain fields instead: classification +
 * confidence.
 * (The detection-row storyboard content lands in PR6; until then the row
 * inherits the Events-row conventions in storyboard Flow 02.)
 *
 * Both helpers are pure + total — they never throw, returning the
 * :data:`DETECTION_DISPLAY_PLACEHOLDER` em-dash for the absent/null case
 * so the row always has something legible to show.
 *
 * Cross-references:
 *   - REQ-UI-061 (Detections panel).
 *   - REQ-INGEST-044 (nullable confidence / classification — both are
 *     normal absences, not errors).
 *   - ADR-032 (Detection supersedes Event).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Marker conventions" (detection row inherits the event-row
 *     time / classification-or-priority / producer convention; the
 *     detection-specific storyboard section lands in PR6).
 */

/** The em-dash shown for a null/absent display field. */
export const DETECTION_DISPLAY_PLACEHOLDER = "—";

/** The label shown for a detection that carried no classification. */
export const UNCLASSIFIED_LABEL = "Unclassified";

/**
 * Format a detection confidence in [0, 1] as a whole-percent string
 * (e.g., ``0.92`` → ``"92%"``). Returns :data:`DETECTION_DISPLAY_PLACEHOLDER`
 * when ``confidence`` is ``null`` (the source omitted it) or not a finite
 * number (defensive against a malformed payload that slipped the wire
 * guard). Rounds to the nearest integer percent — the operator reads a
 * coarse confidence, and sub-percent precision is noise at a glance.
 */
export function formatConfidence(confidence: number | null): string {
  if (confidence === null || !Number.isFinite(confidence)) {
    return DETECTION_DISPLAY_PLACEHOLDER;
  }
  return `${Math.round(confidence * 100)}%`;
}

/**
 * The operator-facing classification label for a detection. Returns the
 * raw ``primary_class`` when present, else :data:`UNCLASSIFIED_LABEL` —
 * a detection with no classification is a valid, common state (a bare
 * track with position but no inferred type), not an error.
 */
export function classificationLabel(primaryClass: string | null): string {
  return primaryClass ?? UNCLASSIFIED_LABEL;
}

/**
 * One shared ``Intl.DateTimeFormat`` for the timeline rows' wall clock.
 *
 * ``Date.prototype.toLocaleTimeString(undefined, options)`` builds a fresh
 * formatter on every call (~21 µs on V8 versus ~1.4 µs for a cached
 * ``format``), and the Detections panel called it once per row on every
 * render — 44 % of main-thread self time in the 2026-10-07 site profile under
 * track load (feature spec ``ui-track-load-headroom`` §1.4 / §2.2). The
 * ``chrome/clocks.tsx`` idiom — one memoised formatter — applied here. The
 * output is byte-identical to the previous call: ``toLocaleTimeString`` with
 * these three options delegates to exactly this ``DateTimeFormat``.
 */
const DETECTION_CLOCK_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** Compact wall-clock (HH:MM:SS) for the timeline rows (mirrors the Events panel). */
export function formatDetectionClock(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return DETECTION_CLOCK_FORMAT.format(parsed);
}
